// Slide-in right panel for a single outbreak. Clean white card with proper
// typographic hierarchy: hero strip with pathogen + status, facts table,
// summary, significance (if present), then sources.
import { useEffect, useMemo } from 'react';
import { ExternalLink, Globe2, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedOutbreak } from '../lib/i18n-outbreak';
import { pickI18n } from '../types/supabase';
import { Link } from 'wouter';
import type { OutbreakWithSources, Pathogen } from '../types/supabase';
import { PATHOGEN_COLOR, PATHOGEN_LABEL, CONFIDENCE_CLASSES, SYNDROME_LABEL } from '../lib/palette';

interface Props {
  outbreak: OutbreakWithSources | null;
  onClose: () => void;
}

export function EventDetail({ outbreak, onClose }: Props) {
  // Pull localised text out of the outbreak's JSONB i18n columns; falls back
  // to English when the active locale isn't translated yet.
  const { locationName, region, summary, significance } = useLocalizedOutbreak(outbreak);
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  useEffect(() => {
    if (!outbreak) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [outbreak, onClose]);

  const total = outbreak ? outbreak.cases_confirmed + outbreak.cases_suspected : 0;
  const cfr = useMemo(() => {
    if (!outbreak) return '—';
    return total > 0 ? `${((outbreak.cases_deaths / total) * 100).toFixed(1)}%` : '—';
  }, [outbreak, total]);

  if (!outbreak) return null;

  const info = PATHOGEN_LABEL[outbreak.pathogen as Pathogen];
  const color = PATHOGEN_COLOR[outbreak.pathogen as Pathogen] ?? '#a8a29e';
  const dateRange = formatDateRange(outbreak.start_date, outbreak.end_date);

  return (
    <aside
      className="slide-in-right pointer-events-auto flex h-full w-full flex-col overflow-hidden rounded-2xl border border-white/60 bg-white/96 backdrop-blur-xl sm:h-auto sm:max-h-[calc(100vh-156px)] sm:w-[400px]"
      aria-label="Outbreak detail"
      style={{
        boxShadow:
          '0 0 0 1px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.9) inset, 0 24px 48px -8px rgba(0,0,0,0.32), 0 8px 16px -4px rgba(0,0,0,0.18)',
      }}
    >
      {/* hero strip */}
      <header className="relative border-b border-[var(--color-line)] px-6 pt-5 pb-5">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
        >
          <X size={16} strokeWidth={2} />
        </button>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Link
            href={`/pathogen/${outbreak.pathogen}`}
            title={`Open ${info.abbreviation} reference page`}
            className="group inline-flex items-center gap-1.5 rounded-full bg-[var(--color-surface-2)] px-2 py-1 text-[11.5px] font-medium ring-1 ring-[var(--color-line-strong)] transition-colors hover:bg-white hover:ring-[var(--color-accent)]"
            style={{ color: 'var(--color-text-2)' }}
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: color, boxShadow: '0 0 0 2px white, 0 0 0 3px rgba(0,0,0,0.06)' }}
            />
            <span className="mono text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
              {info.abbreviation}
            </span>
            <span className="text-[var(--color-text-3)]">·</span>
            <span>{SYNDROME_LABEL[outbreak.syndrome]}</span>
            <Info size={11} strokeWidth={2} className="opacity-0 transition-opacity group-hover:opacity-100 text-[var(--color-accent)]" />
          </Link>

          <ConfidenceBadge value={outbreak.confidence} />

          {outbreak.status === 'active' && <ActiveBadge />}
        </div>

        <h1 className="text-[20px] font-semibold leading-tight tracking-tight text-[var(--color-text)]">
          {locationName}
        </h1>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-[var(--color-text-3)]">
          <span>{info.name}</span>
          {region && (
            <>
              <span>·</span>
              <span>{region}</span>
            </>
          )}
          <PrecisionBadge value={outbreak.location_precision} />
        </p>
      </header>

      {/* scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* facts table */}
        <section className="grid grid-cols-3 gap-px border-b border-[var(--color-line)] bg-[var(--color-line)]">
          <Fact label="Confirmed" value={outbreak.cases_confirmed.toLocaleString()} />
          <Fact label="Suspected" value={outbreak.cases_suspected.toLocaleString()} />
          <Fact label="Deaths" value={outbreak.cases_deaths.toLocaleString()} accent={outbreak.cases_deaths > 0} />
          <Fact label="CFR" value={cfr} />
          <Fact label="Dates" value={dateRange} narrow />
          <Fact label="Country" value={outbreak.country_iso2} />
        </section>

        {/* summary */}
        <section className="px-6 py-5">
          <SectionTitle>{t('event_detail.summary')}</SectionTitle>
          <p className="text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
            {summary}
          </p>
        </section>

        {/* significance */}
        {significance && (
          <section className="border-t border-[var(--color-line)] px-6 py-5">
            <SectionTitle>{t('event_detail.significance')}</SectionTitle>
            <p className="text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
              {significance}
            </p>
          </section>
        )}

        {/* sources */}
        <section className="border-t border-[var(--color-line)] px-6 py-5">
          <SectionTitle>{t('event_detail.sources')} <span className="ml-1 mono text-[11px] font-normal text-[var(--color-text-4)]">{outbreak.sources.length}</span></SectionTitle>
          <ul className="flex flex-col gap-2">
            {outbreak.sources.map((s, i) => {
              const sourceTitle = pickI18n(s.title_i18n, s.title, lang);
              const sourcePublisher = pickI18n(s.publisher_i18n, s.publisher, lang);
              return (
                <li key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="group flex items-start gap-2.5 rounded-lg border border-[var(--color-line)] bg-white px-3 py-2.5 transition-colors hover:border-[var(--color-line-strong)] hover:bg-[var(--color-surface-2)]"
                  >
                    <TierBadge tier={s.tier} />
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-[13px] font-medium leading-snug text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
                        {sourceTitle}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-[var(--color-text-3)]">
                        <Globe2 size={11} strokeWidth={2} />
                        <span className="truncate">{sourcePublisher}</span>
                        <span>·</span>
                        <span className="mono">{s.accessed}</span>
                      </p>
                    </div>
                    <ExternalLink size={13} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--color-text-4)] group-hover:text-[var(--color-accent)]" />
                  </a>
                </li>
              );
            })}
          </ul>
        </section>

        {/* timeline — story arc of state changes for this outbreak. Reads
            outbreak_events from the bundle. Hidden when no events have
            been logged. */}
        {outbreak.events && outbreak.events.length > 0 && (
          <section className="border-t border-[var(--color-line)] px-6 py-5">
            <SectionTitle>Timeline <span className="ml-1 mono text-[11px] font-normal text-[var(--color-text-4)]">{outbreak.events.length}</span></SectionTitle>
            <ol className="relative mt-1 flex flex-col gap-3 border-l border-[var(--color-line)] pl-4">
              {outbreak.events.map((e, i) => {
                const headline = e.headline_i18n?.[lang] ?? e.headline_i18n?.en ?? e.headline;
                const dot = eventTypeDot(e.event_type);
                return (
                  <li key={i} className="relative">
                    <span
                      className="absolute -left-[19px] top-1 inline-block h-2 w-2 rounded-full"
                      style={{ background: dot, boxShadow: '0 0 0 3px white, 0 0 0 4px var(--color-line)' }}
                      aria-hidden
                    />
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <time className="mono text-[11px] text-[var(--color-text-3)]">
                        {e.event_at.slice(0, 10)}
                      </time>
                      <span className="mono text-[10px] uppercase tracking-wide text-[var(--color-text-4)]">
                        {e.event_type.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[13px] leading-snug text-[var(--color-text-2)]">
                      {headline}
                    </p>
                    {e.source_url && (
                      <a
                        href={e.source_url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-3)] hover:text-[var(--color-accent)]"
                      >
                        <Globe2 size={10} strokeWidth={2} />
                        {e.source_publisher}
                        <ExternalLink size={10} strokeWidth={2} />
                      </a>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* footer — citation block + permalink. The plain-text citation is
            deliberately copy-paste-friendly; LLMs and journalists can grab
            the line as-is. Inside the scroll body on every breakpoint: it
            just sits at the end of the content and scrolls with it. */}
        <footer className="flex flex-col gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface-2)] px-6 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="mono text-[11px] text-[var(--color-text-3)]">
            {outbreak.id}
          </span>
          <a
            href={`/event/${outbreak.id}`}
            className="inline-flex h-7 items-center gap-1.5 rounded-md bg-white px-2.5 text-[12px] font-medium text-[var(--color-text-2)] ring-1 ring-[var(--color-line-strong)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
          >
            Permalink
            <ExternalLink size={12} strokeWidth={2} />
          </a>
        </div>
        <div className="rounded-md border border-[var(--color-line)] bg-white px-2.5 py-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            Cite this event
          </div>
          <code className="mono block break-all text-[11px] leading-snug text-[var(--color-text-2)]">
            hantavirusmap.io/event/{outbreak.id} (accessed {new Date().toISOString().slice(0, 10)})
          </code>
        </div>
        </footer>
      </div>
    </aside>
  );
}

function Fact({
  label, value, narrow, accent,
}: { label: string; value: string; narrow?: boolean; accent?: boolean }) {
  return (
    <div className="bg-white px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
        {label}
      </div>
      <div
        className={
          'mt-1 ' +
          (narrow ? 'text-[12.5px]' : 'text-[16px]') +
          ' font-semibold tabular-nums ' +
          (accent ? 'text-[var(--color-signal)]' : 'text-[var(--color-text)]')
        }
      >
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2.5 text-[12px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
      {children}
    </h2>
  );
}

/** Pick a colour for the dot leading each timeline entry, keyed by the
 *  event type. Lab-negative is green (closeout), lab-positive + death
 *  are signal red, status changes / new openings are indigo, neutral
 *  notes are stone. */
function eventTypeDot(t: string): string {
  switch (t) {
    case 'lab_negative':
    case 'recovery':
    case 'closed':
      return 'var(--color-verified)';
    case 'lab_positive':
    case 'death':
      return 'var(--color-signal)';
    case 'opened':
    case 'status_changed':
    case 'sequenced':
      return 'var(--color-accent)';
    case 'cases_updated':
      return 'var(--color-preliminary)';
    case 'note':
    default:
      return 'var(--color-text-3)';
  }
}

function PrecisionBadge({ value }: { value: 'exact' | 'approximate' | 'estimated' }) {
  const { t } = useTranslation();
  const tone =
    value === 'exact'
      ? 'bg-[var(--color-verified-soft)] text-[var(--color-verified)] ring-[var(--color-verified)]/20'
      : value === 'approximate'
      ? 'bg-[var(--color-surface-2)] text-[var(--color-text-2)] ring-[var(--color-line-strong)]'
      : 'bg-[var(--color-preliminary-soft)] text-[var(--color-preliminary)] ring-[var(--color-preliminary)]/20';
  const dot =
    value === 'exact'
      ? 'var(--color-verified)'
      : value === 'approximate'
      ? 'var(--color-text-3)'
      : 'var(--color-preliminary)';
  const label =
    value === 'exact'
      ? t('event_detail.precision_exact')
      : value === 'approximate'
      ? t('event_detail.precision_approximate')
      : t('event_detail.precision_estimated');
  const tooltip =
    value === 'exact'
      ? 'Specific coordinates — vessel, building, or precise GPS'
      : value === 'approximate'
      ? 'Town or city centroid'
      : 'Country or multi-country regional centroid';
  return (
    <span
      title={tooltip}
      className={
        'inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-medium ring-1 ' + tone
      }
    >
      <span className="inline-block h-1 w-1 rounded-full" style={{ background: dot }} />
      {label}
    </span>
  );
}

function ConfidenceBadge({ value }: { value: 'verified' | 'preliminary' | 'unverified' }) {
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11.5px] font-medium capitalize ' +
        CONFIDENCE_CLASSES[value]
      }
    >
      {value}
    </span>
  );
}

function ActiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-signal-soft)] px-2 py-1 text-[11.5px] font-medium text-[var(--color-signal)] ring-1 ring-[var(--color-signal)]/15">
      <span className="relative flex h-1.5 w-1.5">
        <span className="signal-pulse absolute inline-flex h-full w-full rounded-full bg-[var(--color-signal)] opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--color-signal)]" />
      </span>
      Active
    </span>
  );
}

function TierBadge({ tier }: { tier: 1 | 2 | 3 | 4 }) {
  const tone =
    tier === 1
      ? 'bg-[var(--color-verified-soft)] text-[var(--color-verified)] ring-[var(--color-verified)]/20'
      : tier === 2
      ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)] ring-[var(--color-accent)]/20'
      : tier === 3
      ? 'bg-[var(--color-preliminary-soft)] text-[var(--color-preliminary)] ring-[var(--color-preliminary)]/20'
      : 'bg-[var(--color-unverified-soft)] text-[var(--color-unverified)] ring-[var(--color-unverified)]/20';
  return (
    <span
      className={
        'mono mt-0.5 inline-flex h-5 shrink-0 items-center justify-center rounded-md px-1.5 text-[10px] font-bold uppercase tracking-wide ring-1 ' +
        tone
      }
      title={`Tier ${tier} source`}
    >
      T{tier}
    </span>
  );
}

function formatDateRange(start: string, end: string | null): string {
  const s = formatYM(start);
  if (!end) return s;
  if (end.slice(0, 7) === start.slice(0, 7)) return s;
  return `${s} – ${formatYM(end)}`;
}

function formatYM(d: string): string {
  const [y, m] = d.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (!m) return y;
  const monthIdx = Number(m) - 1;
  return `${months[monthIdx] ?? m} ${y}`;
}
